'use client';

import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import WhatsAppFloat from '@/components/WhatsAppFloat';
import AppLogo from '@/components/ui/AppLogo';
import CorporateClientsSection from '@/app/components/CorporateClientsSection';
import {
  Award,
  Target,
  Eye,
  Heart,
  Shield,
  Users,
  FlaskConical,
  CheckCircle,
  Star,
  Calendar,
  ArrowRight,
  Microscope,
  Clock,
  TrendingUp,
  Zap } from
'lucide-react';

const coreValues = [
{
  icon: Shield,
  title: 'Accuracy & Precision',
  description: 'Every test is conducted with meticulous care using state-of-the-art automated analyzers, ensuring results you can trust for critical health decisions.',
  color: 'bg-blue-50 text-blue-700 border-blue-100',
  iconBg: 'bg-blue-100'
},
{
  icon: Heart,
  title: 'Patient-First Care',
  description: 'From home collection to report delivery, every touchpoint is designed around patient comfort, dignity, and convenience.',
  color: 'bg-rose-50 text-rose-700 border-rose-100',
  iconBg: 'bg-rose-100'
},
{
  icon: Zap,
  title: 'Speed & Reliability',
  description: 'Turnaround times that match clinical urgency — routine reports in 6 hours, urgent panels in 2 hours, with digital delivery.',
  color: 'bg-amber-50 text-amber-700 border-amber-100',
  iconBg: 'bg-amber-100'
},
{
  icon: TrendingUp,
  title: 'Continuous Improvement',
  description: 'Regular internal audits, external quality assessments, and ongoing staff training keep our standards ahead of industry benchmarks.',
  color: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  iconBg: 'bg-emerald-100'
},
{
  icon: Users,
  title: 'Community Commitment',
  description: 'Affordable pricing, free health camps, and corporate wellness programs reflect our commitment to making quality diagnostics accessible to all.',
  color: 'bg-purple-50 text-purple-700 border-purple-100',
  iconBg: 'bg-purple-100'
},
{
  icon: FlaskConical,
  title: 'Scientific Excellence',
  description: 'NABL-accredited processes, ISO-certified workflows, and AERB-approved radiation safety protocols underpin every diagnostic service.',
  color: 'bg-sky-50 text-sky-700 border-sky-100',
  iconBg: 'bg-sky-100'
}];


const teamMembers = [
{
  name: 'Dr. Rajesh Sharma',
  role: 'Chief Pathologist & Director',
  qualification: 'MD Pathology, AIIMS Delhi',
  experience: '22 Years Experience',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ea2bb1c0-1767468203453.png",
  alt: 'Senior Indian male pathologist in white coat with stethoscope at laboratory'
},
{
  name: 'Dr. Priya Mehta',
  role: 'Senior Biochemist',
  qualification: 'PhD Biochemistry, Delhi University',
  experience: '15 Years Experience',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_189bf8c75-1772076983840.png",
  alt: 'Indian female doctor in white coat smiling professionally'
},
{
  name: 'Dr. Anil Gupta',
  role: 'Radiologist',
  qualification: 'MD Radiology, PGIMER',
  experience: '18 Years Experience',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e2c7ee46-1783512186330.png",
  alt: 'Indian male radiologist in white coat reviewing scan results'
},
{
  name: 'Dr. Sunita Verma',
  role: 'Microbiologist',
  qualification: 'MD Microbiology, BHU',
  experience: '12 Years Experience',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_13ac3b046-1763300274943.png",
  alt: 'Indian female microbiologist in lab coat working in laboratory'
}];


const facilities = [
{
  title: 'Automated Hematology Lab',
  description: 'Fully automated 5-part differential CBC analyzers with QC protocols for precise blood count analysis.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_13ffb41a5-1772880606813.png",
  alt: 'Modern automated hematology laboratory with advanced blood analyzers and equipment'
},
{
  title: 'Biochemistry & Immunology',
  description: 'High-throughput biochemistry analyzers for liver, kidney, thyroid, cardiac, and hormonal panels.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e46c4273-1784859504441.png",
  alt: 'Biochemistry laboratory with automated analyzers and test tubes in Indian diagnostic center'
},
{
  title: 'Digital Radiology Suite',
  description: 'AERB-approved digital X-Ray, Ultrasound, and 2D Echo with PACS-integrated reporting.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fd25861b-1767775549929.png",
  alt: 'Modern digital radiology room with X-ray equipment and imaging workstation'
},
{
  title: 'Patient Reception & Waiting',
  description: 'Air-conditioned, comfortable reception with digital token system and dedicated sample collection booths.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f070ca21-1779382472171.png",
  alt: 'Modern hospital reception area with comfortable seating and Indian medical staff at desk'
}];


const milestones = [
{ year: '2012', event: 'Founded in Noida Sector 51 with a vision to provide NABL-grade diagnostics' },
{ year: '2015', event: 'Received NABL Accreditation — a landmark in diagnostic quality' },
{ year: '2017', event: 'Launched Corporate Health Checkup division serving 50+ companies' },
{ year: '2019', event: 'Expanded services to include Endoscopy & Mammography' },
{ year: '2021', event: 'Introduced Home Sample Collection across all Noida sectors' },
{ year: '2023', event: 'Crossed 5 lakh patients served; launched digital report portal' },
{ year: '2025', event: 'NABL Accreditation renewed (MC-6214, ISO 15189:2022); valid until December 2029' }];


const nablHighlights = [
'Accreditation No: NABL MC-6214',
'ISO 15189:2022 Medical Laboratory Standard',
'Valid: 26 December 2025 – 25 December 2029',
'AERB Approved Radiation Safety',
'External Quality Assessment (EQA) participant',
'EQAS — CMC Vellore & AIIMS Delhi',
'Regular CAP proficiency testing'];


export default function AboutPage() {
  return (
    <>
      <Header />
      <main>
        {/* Hero Banner */}
        <section className="relative min-h-[420px] flex items-center overflow-hidden">
          <Image
            src="https://img.rocket.new/generatedImages/rocket_gen_img_1ab7c7a30-1784043238264.png"
            alt="Modern Indian diagnostic laboratory interior with advanced equipment and professional staff at 51 Plus Lab Sector 51 Noida"
            fill
            className="object-cover object-center"
            priority />
          
          <div className="absolute inset-0 bg-gradient-to-r from-[#0c2d6b]/90 via-[#0c2d6b]/70 to-transparent" />
          {/* Watermark logo */}
          <div className="absolute right-8 top-1/2 -translate-y-1/2 opacity-10 hidden lg:block">
            <AppLogo size={360} />
          </div>
          <div className="relative z-10 max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 py-20">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/20 text-white text-xs font-semibold px-3 py-1.5 rounded-full mb-5 uppercase tracking-wider">
                <Award size={13} />
                NABL Accredited · Est. 2012
              </div>
              <h1 className="text-4xl md:text-5xl font-extrabold text-white leading-tight mb-4">
                About <span className="text-sky-300">51 Plus Lab</span>
              </h1>
              <p className="text-white/80 text-lg leading-relaxed mb-8 max-w-xl">
                Fifty One Plus Lab (Powered by BeLife) is committed to providing the entire gamut of most advanced laboratory services and exceptional support services for boosting the community&apos;s health service sector. Located at H-56, Sector 51, Noida, we are NABL-accredited, CGHS-empanelled, and serve 50+ corporate clients with pre-employment medicals, annual health checkups, and comprehensive diagnostics.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link href="/book-appointment" className="btn-primary">
                  <Calendar size={15} />
                  Book a Test
                </Link>
                <a href="#nabl" className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/30 text-white font-semibold px-5 py-2.5 rounded-xl hover:bg-white/25 transition-colors text-sm">
                  <Award size={15} />
                  Our Accreditations
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Quick Stats */}
        <section className="bg-primary py-8">
          <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center text-white">
              {[
              { value: '5 Lakh+', label: 'Patients Served' },
              { value: '13+', label: 'Years of Excellence' },
              { value: '1500+', label: 'Tests Available' },
              { value: '50+', label: 'Corporate Clients' }]?.
              map((stat) =>
              <div key={stat?.label}>
                  <div className="text-3xl font-extrabold text-sky-200">{stat?.value}</div>
                  <div className="text-sm text-white/70 font-medium mt-1">{stat?.label}</div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="py-20 bg-background">
          <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Image */}
              <div className="relative">
                <div className="relative rounded-2xl overflow-hidden h-[420px]">
                  <Image
                    src="https://img.rocket.new/generatedImages/rocket_gen_img_132dcfe90-1784889806780.png"
                    alt="Indian pathologist examining blood samples under microscope in modern laboratory at 51 Plus Lab Sector 51 Noida"
                    fill
                    className="object-cover" />
                  
                </div>
                {/* Floating badge */}
                <div className="absolute -bottom-5 -right-5 bg-white rounded-2xl shadow-xl p-4 border border-border flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Award size={24} className="text-primary" />
                  </div>
                  <div>
                    <div className="font-bold text-sm text-foreground">NABL Accredited</div>
                    <div className="text-xs text-muted-foreground">MC-6214 · Valid till Dec 2029</div>
                  </div>
                </div>
              </div>

              {/* Mission & Vision text */}
              <div>
                <div className="inline-flex items-center gap-2 bg-primary/10 text-primary text-xs font-bold px-3 py-1.5 rounded-full mb-5 uppercase tracking-wider">
                  <Star size={12} />
                  Who We Are
                </div>
                <h2 className="text-3xl md:text-4xl font-extrabold text-foreground mb-6 leading-tight">
                  Diagnostics Built on <span className="text-primary">Trust & Science</span>
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-8">
                  Founded in 2012, 51 Plus Lab began with a single vision: to bring hospital-grade diagnostic accuracy to every resident of Noida at an affordable price. Today, we operate our fully equipped branch at H-56, Sector 51, Noida, serve 50+ corporate clients, and have earned the trust of over 5 lakh patients.
                </p>

                {/* Mission */}
                <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5 mb-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-9 h-9 rounded-xl bg-blue-100 flex items-center justify-center">
                      <Target size={18} className="text-blue-700" />
                    </div>
                    <h3 className="font-bold text-blue-900 text-base">Our Mission</h3>
                  </div>
                  <p className="text-blue-800 text-sm leading-relaxed">
                    To provide accurate, timely, and affordable diagnostic services that empower patients and physicians to make informed health decisions — delivered with compassion and scientific rigour.
                  </p>
                </div>

                {/* Vision */}
                <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-9 h-9 rounded-xl bg-emerald-100 flex items-center justify-center">
                      <Eye size={18} className="text-emerald-700" />
                    </div>
                    <h3 className="font-bold text-emerald-900 text-base">Our Vision</h3>
                  </div>
                  <p className="text-emerald-800 text-sm leading-relaxed">
                    To be Delhi NCR&apos;s most trusted diagnostic partner — recognised for clinical excellence, patient-centric service, and a commitment to making preventive healthcare a way of life.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* NABL Accreditation */}
        <section id="nabl" className="py-20 bg-gradient-to-br from-[#0c2d6b] to-[#1a4a9e] relative overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div className="absolute top-10 right-10 w-64 h-64 rounded-full bg-white" />
            <div className="absolute bottom-10 left-10 w-48 h-48 rounded-full bg-white" />
          </div>
          <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 relative z-10">
            <div className="grid lg:grid-cols-2 gap-14 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-white/15 border border-white/20 text-white text-xs font-bold px-3 py-1.5 rounded-full mb-5 uppercase tracking-wider">
                  <Award size={12} />
                  Quality Assurance
                </div>
                <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4 leading-tight">
                  NABL Accredited <br />
                  <span className="text-sky-300">Diagnostic Excellence</span>
                </h2>
                <p className="text-white/75 leading-relaxed mb-8">
                  NABL (National Accreditation Board for Testing and Calibration Laboratories) accreditation is the highest quality standard for diagnostic labs in India. Our accreditation (MC-6214) means every test you receive has been validated against international ISO 15189:2022 standards. Certificate issued on 26 December 2025, valid until 25 December 2029.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {nablHighlights?.map((item) =>
                  <div key={item} className="flex items-start gap-2.5">
                      <CheckCircle size={16} className="text-sky-300 flex-shrink-0 mt-0.5" />
                      <span className="text-white/85 text-sm">{item}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Accreditation cards */}
              <div className="grid grid-cols-2 gap-4">
                {[
                { badge: 'NABL', sub: 'MC-6214', desc: 'ISO 15189:2022 Medical Testing', icon: Award },
                { badge: 'ISO', sub: '15189:2022', desc: 'Medical Laboratory Standard', icon: Shield },
                { badge: 'AERB', sub: 'Approved', desc: 'Radiation Safety Certified', icon: Zap },
                { badge: 'EQA', sub: 'Enrolled', desc: 'External Quality Assessment', icon: Microscope }]?.
                map((card) =>
                <div key={card?.badge} className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-5 text-center hover:bg-white/15 transition-colors">
                    <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center mx-auto mb-3">
                      <card.icon size={22} className="text-sky-300" />
                    </div>
                    <div className="text-2xl font-extrabold text-white">{card?.badge}</div>
                    <div className="text-sky-300 font-bold text-sm">{card?.sub}</div>
                    <div className="text-white/60 text-xs mt-1 leading-snug">{card?.desc}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="py-20 bg-background">
          <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10">
            <div className="text-center mb-14">
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary text-xs font-bold px-3 py-1.5 rounded-full mb-4 uppercase tracking-wider">
                <Heart size={12} />
                What Drives Us
              </div>
              <h2 className="text-3xl md:text-4xl font-extrabold text-foreground mb-3">
                Our Core Values
              </h2>
              <p className="text-muted-foreground max-w-xl mx-auto text-base">
                Six principles that guide every decision, every test, and every patient interaction at 51 Plus Lab.
              </p>
            </div>

            {/* Bento-style asymmetric grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {coreValues?.map((value, idx) =>
              <div
                key={value?.title}
                className={`rounded-2xl border p-6 ${value?.color} ${idx === 0 ? 'lg:col-span-1 lg:row-span-1' : ''}`}>
                
                  <div className={`w-11 h-11 rounded-xl ${value?.iconBg} flex items-center justify-center mb-4`}>
                    <value.icon size={20} />
                  </div>
                  <h3 className="font-bold text-base mb-2">{value?.title}</h3>
                  <p className="text-sm leading-relaxed opacity-80">{value?.description}</p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Lab Facilities */}
        <section className="py-20 bg-muted/30">
          <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10">
            <div className="text-center mb-14">
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary text-xs font-bold px-3 py-1.5 rounded-full mb-4 uppercase tracking-wider">
                <Microscope size={12} />
                Infrastructure
              </div>
              <h2 className="text-3xl md:text-4xl font-extrabold text-foreground mb-3">
                World-Class Lab Facilities
              </h2>
              <p className="text-muted-foreground max-w-xl mx-auto text-base">
                Equipped with the latest diagnostic technology to ensure precision at every step.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {facilities?.map((facility) =>
              <div key={facility?.title} className="bg-card rounded-2xl overflow-hidden border border-border shadow-sm hover:shadow-md transition-shadow group">
                  <div className="relative h-52 overflow-hidden">
                    <Image
                    src={facility?.image}
                    alt={facility?.alt}
                    fill
                    className="object-cover group-hover:scale-105 transition-transform duration-500" />
                  
                    <div className="absolute inset-0 bg-gradient-to-t from-foreground/60 to-transparent" />
                    <div className="absolute bottom-4 left-4">
                      <h3 className="text-white font-bold text-lg">{facility?.title}</h3>
                    </div>
                  </div>
                  <div className="p-5">
                    <p className="text-muted-foreground text-sm leading-relaxed">{facility?.description}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Team Section */}
        <section className="py-20 bg-background">
          <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10">
            <div className="text-center mb-14">
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary text-xs font-bold px-3 py-1.5 rounded-full mb-4 uppercase tracking-wider">
                <Users size={12} />
                Our Experts
              </div>
              <h2 className="text-3xl md:text-4xl font-extrabold text-foreground mb-3">
                Meet the Team
              </h2>
              <p className="text-muted-foreground max-w-xl mx-auto text-base">
                Experienced specialists committed to diagnostic accuracy and patient care.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {teamMembers?.map((member) =>
              <div key={member?.name} className="bg-card rounded-2xl border border-border overflow-hidden shadow-sm hover:shadow-md transition-shadow group text-center">
                  <div className="relative h-52 overflow-hidden bg-muted">
                    <Image
                    src={member?.image}
                    alt={member?.alt}
                    fill
                    className="object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                  
                  </div>
                  <div className="p-5">
                    <h3 className="font-bold text-foreground text-base">{member?.name}</h3>
                    <p className="text-primary text-sm font-semibold mt-0.5">{member?.role}</p>
                    <p className="text-muted-foreground text-xs mt-1">{member?.qualification}</p>
                    <div className="mt-3 inline-flex items-center gap-1.5 bg-primary/10 text-primary text-xs font-semibold px-3 py-1 rounded-full">
                      <Clock size={11} />
                      {member?.experience}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Journey / Milestones */}
        <section className="py-20 bg-muted/30">
          <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10">
            <div className="grid lg:grid-cols-2 gap-14 items-start">
              <div>
                <div className="inline-flex items-center gap-2 bg-primary/10 text-primary text-xs font-bold px-3 py-1.5 rounded-full mb-5 uppercase tracking-wider">
                  <TrendingUp size={12} />
                  Our Journey
                </div>
                <h2 className="text-3xl md:text-4xl font-extrabold text-foreground mb-4 leading-tight">
                  13 Years of <span className="text-primary">Diagnostic Excellence</span>
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-8">
                  From a single vision to Noida&apos;s most trusted diagnostic lab — every milestone reflects our commitment to quality and community health at Sector 51, Noida.
                </p>
                <div className="relative h-64 rounded-2xl overflow-hidden">
                  <Image
                    src="https://img.rocket.new/generatedImages/rocket_gen_img_1773536a7-1784889806793.png"
                    alt="Indian medical laboratory team in white coats discussing patient reports in modern diagnostic center at 51 Plus Lab Sector 51 Noida"
                    fill
                    className="object-cover" />
                  
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0c2d6b]/60 to-transparent" />
                  <div className="absolute bottom-4 left-4 text-white">
                    <div className="font-bold text-lg">5 Lakh+ Patients</div>
                    <div className="text-white/70 text-sm">Trusted across Noida since 2012</div>
                  </div>
                </div>
              </div>

              {/* Timeline — horizontal card style, not vertical numbered list */}
              <div className="space-y-3">
                {milestones?.map((m) =>
                <div key={m?.year} className="flex items-start gap-4 bg-card border border-border rounded-xl px-5 py-4 hover:border-primary/30 hover:shadow-sm transition-all group">
                    <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-colors">
                      <span className="font-extrabold text-sm text-primary group-hover:text-white">{m?.year}</span>
                    </div>
                    <div className="flex-1 pt-1">
                      <p className="text-foreground text-sm leading-relaxed">{m?.event}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Corporate Clients */}
        <CorporateClientsSection variant="homepage" showCarousel={false} />

        {/* CTA */}
        <section className="py-16 bg-primary relative overflow-hidden">
          <div className="absolute right-0 top-0 bottom-0 w-1/3 opacity-10 hidden lg:block">
            <AppLogo size={400} className="absolute right-8 top-1/2 -translate-y-1/2" />
          </div>
          <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 relative z-10">
            <div className="max-w-2xl">
              <h2 className="text-3xl font-extrabold text-white mb-3">
                Experience the 51 Plus Lab Difference
              </h2>
              <p className="text-white/75 mb-8 leading-relaxed">
                Book your health checkup today and discover why over 5 lakh patients trust us for accurate, affordable diagnostics at H-56, Sector 51, Noida.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link href="/book-appointment" className="inline-flex items-center gap-2 bg-white text-primary font-bold px-6 py-3 rounded-xl hover:bg-sky-50 transition-colors text-sm">
                  <Calendar size={16} />
                  Book a Test Now
                </Link>
                <Link href="/health-packages" className="inline-flex items-center gap-2 bg-white/15 border border-white/30 text-white font-semibold px-6 py-3 rounded-xl hover:bg-white/25 transition-colors text-sm">
                  View Health Packages
                  <ArrowRight size={15} />
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppFloat />
    </>
  );


}